import { createContext, use, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { io, type Socket } from "socket.io-client";
import { platform } from "../platform/index.js";
import { useRuntimeConfig } from "../config-context.js";

/**
 * **القناةُ اللحظية في الواجهة — موضعٌ واحد يملك المقبس.**
 *
 * **ولا يُفتَح مقبسٌ في شاشة**: فلو فتحت كلُّ شاشةٍ مقبسَها لصار على الموظّف
 * أربعةُ اتصالاتٍ لنفس السيرفر — وهو ما كان في v2 بشكلٍ آخر: **سؤالٌ متكرّر كل
 * عشرين ثانيةً من نسختين من الكود نفسه تعملان معاً**.
 *
 * **ولا تحمل الرسائلُ محتوى**: السيرفرُ يقول «هذه القناةُ تغيّرت»، فتُعيد
 * الشاشةُ القراءةَ من مسارها المحروس. **فما لا يُبعَث لا يُسرَّب** — ولا مُسقِطَ
 * ثانٍ يفترق عن الأوّل.
 *
 * **والحضورُ يصل مدفوعاً** — ولا يُسأل عنه: فلا موضعَ في هذا الملفّ يبعث قائمةَ
 * أشخاصٍ ويسأل من فيهم متّصل، **وهو ثقبُ v2 المسجَّل**.
 */

export type PresenceState = "online" | "away" | "offline";

type RealtimeValue = {
  /** حالةُ من يجوز لي أن أرى حضورَه — **مدفوعةً من السيرفر** */
  readonly presence: ReadonlyMap<string, PresenceState>;
  /** **يُراقَب خيطٌ** — ويُقاس الوصولُ في السيرفر قبل الانضمام */
  readonly watch: (channelId: string) => void;
  readonly unwatch: (channelId: string) => void;
  /** **عدّادُ تغيُّرٍ لقناة** — تقرؤه الشاشةُ فتُعيد التحميل */
  readonly changedAt: ReadonlyMap<string, number>;
};

const RealtimeContext = createContext<RealtimeValue | null>(null);

/**
 * **ويُفتَح المقبسُ مرّةً للجلسة الداخلية** — ولا يُفتَح في البورتال أبداً.
 *
 * **فجهةُ الاتصال يرفضها السيرفرُ في المصافحة** (حدُّ البورتال على الجلسة)،
 * **ولا يُحاوِل العميلُ أصلاً**: فمحاولةٌ تُرفَض كلَّ ثانيةٍ ضجيجٌ في السجلّ
 * وبطاريةٌ تُستهلَك بلا مقابل. والقوقعةُ البورتالية لا تُركّب هذا المزوِّد.
 */
export function RealtimeProvider({
  enabled,
  children,
}: {
  /**
   * **ويُفتَح للجلسة الداخلية وحدها** — فالبورتالُ لا يُركّبه أصلاً.
   *
   * **ولا رمزَ يُمرَّر**: الجلسةُ كعكةٌ `HttpOnly` لا تقرؤها الواجهة — وذلك
   * مقصود — **وهي تسافر مع المصافحة** كما تسافر مع أي طلب.
   */
  readonly enabled: boolean;
  readonly children: ReactNode;
}): ReactNode {
  const { apiBaseUrl } = useRuntimeConfig();
  const [presence, setPresence] = useState<ReadonlyMap<string, PresenceState>>(new Map());
  const [changedAt, setChangedAt] = useState<ReadonlyMap<string, number>>(new Map());
  const socketRef = useRef<Socket | null>(null);
  const watched = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!enabled) return;

    /**
     * **وأصلُ المقبس هو أصلُ السيرفر** — يُقرأ وقت التشغيل من `config.json`.
     *
     * **ولا يُخبَز وقت البناء**: تسعُ حوادثَ في v2 سببُها عنوانٌ مخبوز، فنفسُ
     * الحزمة تعمل عند المشتري وفي الديمو وعند TWING.
     */
    const origin = apiBaseUrl.replace(/\/api\/v1\/?$/, "");
    const socket = io(origin, {
      path: "/realtime",
      transports: ["websocket"],
      /** **وتُرسَل الكعكةُ مع المصافحة** — فهي حاملُ الجلسة */
      withCredentials: true,
      /** **ويعود بعد انقطاعٍ بتباطؤ** — فشبكةٌ ساقطةٌ لا تُقصَف بمحاولات */
      reconnectionDelay: 2_000,
      reconnectionDelayMax: 30_000,
    });
    socketRef.current = socket;

    socket.on("connect", () => {
      /** **ويُعاد ما كان مراقَباً** — فانقطاعٌ لا يُفقِد الشاشةَ خيطَها */
      for (const channelId of watched.current) socket.emit("watch", channelId);
    });

    socket.on("presence:snapshot", (rows: readonly { userId: string; state: PresenceState }[]) => {
      setPresence(new Map(rows.map((row) => [row.userId, row.state])));
    });

    socket.on("presence", (row: { userId: string; state: PresenceState }) => {
      setPresence((current) => {
        const next = new Map(current);
        if (row.state === "offline") next.delete(row.userId);
        else next.set(row.userId, row.state);
        return next;
      });
    });

    socket.on("channel:changed", (row: { channelId: string }) => {
      setChangedAt((current) => new Map(current).set(row.channelId, Date.now()));
    });

    /**
     * **و«بعيد» من إخفاء الشاشة** — ويمرّ بموديول المنصّة لا بـ`document` هنا.
     *
     * فقاعدةُ المستودع: **لا عامٌّ من المتصفّح يُنادى مباشرةً** — وهي التي
     * تجعل الاختبارَ ممكناً والتنصيبَ على جهازٍ بلا نافذةٍ ممكناً.
     */
    const stopWatching = platform.onVisibilityChange((hidden) => {
      socket.emit("idle", hidden);
    });

    return () => {
      stopWatching();
      socket.close();
      socketRef.current = null;
    };
  }, [enabled, apiBaseUrl]);

  const value = useMemo<RealtimeValue>(
    () => ({
      presence,
      changedAt,
      watch: (channelId: string) => {
        watched.current.add(channelId);
        socketRef.current?.emit("watch", channelId);
      },
      unwatch: (channelId: string) => {
        watched.current.delete(channelId);
        socketRef.current?.emit("unwatch", channelId);
      },
    }),
    [presence, changedAt],
  );

  return <RealtimeContext value={value}>{children}</RealtimeContext>;
}

/**
 * **ومن لا مزوِّدَ له يقرأ صمتاً** — لا يسقط.
 *
 * **فالبورتالُ لا يُركّب المزوِّد**، وشاشاتُه قد ترسم مكوّنات مشتركة. ورميُ خطأٍ
 * هنا كان سيجعل **مكوّنَ الوجه يُسقط شاشةَ العميل** — وهو بعينه ما يمنعه §7:
 * لا حضورَ في البورتال، **لا انفجارَ في البورتال**.
 */
export function useRealtime(): RealtimeValue {
  return (
    use(RealtimeContext) ?? {
      presence: new Map(),
      changedAt: new Map(),
      watch: () => undefined,
      unwatch: () => undefined,
    }
  );
}
